import {
  getPostHogClient,
  offPostHogClientInit,
  onPostHogClientInit,
} from '@findigs/helpers/src/integrations/posthog/posthog.client';
import type { PostHogClient } from '@findigs/helpers/src/integrations/posthog/posthog.client';
import { startTransition, useEffect } from 'react';
import { useStateWithRef } from '../../hooks/use-state-with-ref';

/**
 * Note: this hook is SSR-safe
 *
 * Consider using the server-side helpers if possible
 * as this hook will cause the rendered content to change after mount
 *
 * (ie. the version rendered server-side will be replaced)
 **/
export function usePostHogClient() {
  const [posthogClient, setPosthogClient, ref] =
    useStateWithRef<PostHogClient | null>(null);

  // this effect will run every re-render, but will skip if the client is already set
  // this is intentional, because getPostHogClient() is not reactive
  useEffect(() => {
    if (ref.current) return;

    let reactReady = false;
    let client = getPostHogClient();
    const listener = () => {
      client ??= getPostHogClient();

      if (reactReady) {
        setPosthogClient(client);
      }
    };

    startTransition(() => {
      reactReady = true;

      if (client) {
        setPosthogClient(client);
      }
    });

    onPostHogClientInit(listener);
    return () => {
      offPostHogClientInit(listener);
    };
  });

  return posthogClient;
}
